import { useEffect, useState, useCallback } from 'react';
import { supabase } from '../utils/supabase';
import { sendSquadMessage } from '../services/squadApi';
import { trackEvent } from '../utils/analytics';

export function useSquadChat(squadId) {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [isSendingMessage, setIsSendingMessage] = useState(false);

  // Fetch initial messages
  useEffect(() => {
    if (!squadId || !supabase) return;

    async function fetchMessages() {
      const { data, error } = await supabase
        .from('squad_messages')
        .select('*')
        .eq('squad_id', squadId)
        .order('created_at', { ascending: true });
      
      if (data) {
        setMessages(data);
      } else if (error) {
        console.error('Could not fetch messages:', error.message);
      }
    }

    fetchMessages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [squadId]); // supabase is a stable import

  const addMessage = useCallback((message) => {
    // Validate message has required fields
    if (!message.id) {
      console.error('Message missing id field, cannot add:', message);
      return;
    }
    
    setMessages((prev) => {
      // Check if message already exists (avoid duplicates)
      const exists = prev.some(m => m.id === message.id);
      if (exists) {
        return prev;
      }
      return [...prev, message];
    });
  }, []);

  // Function to manually refresh messages
  const refreshMessages = useCallback(async () => {
    if (!squadId || !supabase) return;
    
    const { data, error } = await supabase
      .from('squad_messages')
      .select('*')
      .eq('squad_id', squadId)
      .order('created_at', { ascending: true });
    
    if (data) {
      setMessages(data);
    } else if (error) {
      console.error('Error fetching messages:', error);
    }
  }, [squadId]);

  const sendMessage = useCallback(async (attendeeUuid) => {
    if (!squadId || !attendeeUuid || !newMessage.trim() || newMessage.length > 500) return;
    
    const attendeeName = localStorage.getItem('attendee_name') || 'Anonymous';
    const messageLength = newMessage.trim().length;
    setIsSendingMessage(true);

    try {
      await sendSquadMessage({
        squad_id: squadId,
        attendee_uuid: attendeeUuid,
        attendee_name: attendeeName,
        message: newMessage.trim(),
      });

      trackEvent('Squad Message Sent', {
        squad_id: squadId,
        message_length: messageLength,
        total_messages: messages.length + 1,
      });

      setNewMessage('');
      
      // Refresh messages after a short delay to ensure it's in the database
      setTimeout(() => {
        refreshMessages();
      }, 500);
    } catch (error) {
      console.error('Error sending message:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to send message. Please try again.';
      alert(errorMessage);
      trackEvent('Squad Message - Error', {
        squad_id: squadId,
        error_message: error instanceof Error ? error.message : 'Unknown error',
        message_length: messageLength,
      });
    } finally {
      setIsSendingMessage(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [squadId, newMessage, refreshMessages]); // messages.length is intentionally excluded to avoid recreating callback on every message

  return {
    messages,
    newMessage,
    setNewMessage,
    isSendingMessage,
    sendMessage,
    addMessage,
    refreshMessages,
  };
}
